<template>
  <div>
    <b-button
      v-if="auctionContent.status === 'NOT_PAID'"
      v-b-modal.pay-auction-prize
      class="mb-3"
    >
      {{ $t('auction.pay') }}
    </b-button>
    <b-badge
      v-if="auctionContent.status === 'PAID'"
      variant="success"
    >
      {{ $t('auction.paid') }}
    </b-badge>
    <b-badge
      v-else-if="auctionContent.status === 'FINISHED'"
      variant="warning"
    >
      {{ $t('auction.status.FINISHED') }}
    </b-badge>
    <h6>{{ $t('auction.lot_content') }}</h6>
    <a-collapse>
      <a-collapse-panel
        v-for="promotion in auctionContent.lotPromotions"
        :key="promotion.promotionType.name"
        :header="getAuctionTypeTitle(promotion.promotionType.name)"
      >
        <b-button
          :id="`add-product-${promotion.promotionType.name}`"
          v-b-modal.select-product
          title="Tooltip directive content"
          :show="!isPaid"
          :disabled="!isPaid || promotion.freeSlots.length == 0"
          class="mb-3 mr-3"
          @click="() => setPromotionTypeToSelect(promotion.promotionType.name)"
        >
          {{ $t('common.add') }}
        </b-button>
        <b-tooltip
          :disabled="isPaid"
          triggers="hover"
          :target="`add-product-${promotion.promotionType.name}`"
        >
          {{ $t('auction.not_paid') }}
        </b-tooltip>
        <span>{{ `${$t('auction.free')}: ${promotion.freeSlots.length}` }}</span>
        <a-table
          :columns="columns"
          :data-source="promotion.promotionPlaces"
          class="product-table"
          :row-key="(record) => record.id"
          :pagination="false"
        >
          <template
            slot="productName"
            slot-scope="text"
          >
            {{ text ? $l(text) : $t('auction.not_provided') }}
          </template>
          <template
            slot="delete"
            slot-scope="_, record"
          >
            <a-popconfirm
              :title="$t('common.are_you_sure')"
              :ok-text="$t('common.confirm')"
              :cancel-text="$t('common.cancel')"
              @confirm="() => handleDeletePosition(record)"
            >
              <a href="#">{{ $t('common.delete') }}</a>
            </a-popconfirm>
          </template>
        </a-table>
      </a-collapse-panel>
      <a-collapse-panel
        :key="'sectionPlaces'"
        :header="$t('auction.lot_types.category')"
      >
        <a-table
          :data-source="auctionContent.sectionPlaces"
          :columns="sectionColumns"
          :row-key="(record) => record.id"
          :pagination="false"
          :default-expanded-row-keys="defaultExpandedRowKeys"
          expand-row-by-click
        >
          <template
            slot="freeSlots"
            slot-scope="_, record"
          >
            {{ record.freeSlots.length }}
          </template>
          <template
            slot="bindSlots"
            slot-scope="_, record"
          >
            {{ record.sectionPlaces.length }}
          </template>
          <template
            slot="add"
            slot-scope="_, record"
          >
            <b-button
              v-b-modal.select-section-product
              title="Tooltip directive content"
              :show="!isPaid"
              :disabled="!isPaid"
              @click="() => addSectionProduct(record)"
            >
              {{ $t('common.add') }}
            </b-button>
          </template>
          <template
            slot="expandedRowRender"
            slot-scope="record"
            style="margin: 0"
          >
            <div
              v-for="place in record.sectionPlaces"
              :key="place.id"
              class="d-flex align-items-center"
            >
              <span class="mr-3">{{ `№${place.position} - ${$l(place.productName)}` }}</span>
              <a-popconfirm
                :title="$t('common.are_you_sure')"
                :ok-text="$t('common.confirm')"
                :cancel-text="$t('common.cancel')"
                @confirm="() => handleDeletePosition(place, record.sectionId)"
              >
                <a-button type="link">
                  {{ $t('common.delete') }}
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </a-table>
      </a-collapse-panel>
    </a-collapse>

    <b-modal
      id="select-product"
      :title="$t('auction.select_promotions')"
      size="xl"
      :width="900"
      :ok-title="$t('common.confirm')"
      :cancel-title="$t('common.cancel')"
      @ok="handleSubmit"
    >
      <SelectProducts
        :promotions="selectedPromotion"
        :permitted-sections="auctionContent.auctionCategories"
        @change="handleSelect"
      />
    </b-modal>
    <b-modal
      id="select-section-product"
      :title="$t('auction.select_promotions')"
      size="xl"
      :width="900"
      :ok-title="$t('common.confirm')"
      :cancel-title="$t('common.cancel')"
      @ok="handleSubmit"
    >
      <SelectSectionProducts
        :section-place="selectedSection"
        @change="handleSelect"
      />
    </b-modal>
    <b-modal
      id="pay-auction-prize"
      centered
      hide-footer
      :title="$t('auction.payment')"
    >
      <PrizePayment
        :auction-id="settings.hash_id"
        :price="auctionContent.bidList[0] ? auctionContent.bidList[0].price : 0"
      />
    </b-modal>
  </div>
</template>

<script>
import PrizePayment from '../PrizePayment.vue'
import SelectProducts from './SelectProducts.vue'
import SelectSectionProducts from './SelectSectionProducts.vue'
export default {
  name: 'SellerView',
  components: {
    SelectProducts,
    SelectSectionProducts,
    PrizePayment,
  },
  props: {
    categories: {
      type: Array,
      default: () => {},
    },
    settings: {
      type: Object,
      default: () => {},
    },

    auctionContent: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showSelectPromotions: false,
      selectedProducts: [],
      selectedPromotion: null,
      selectedSection: null,
      selectionValid: false,
      columns: [
        {
          title: this.$t('auction.position'),
          dataIndex: 'position',
          width: 100,
        },
        {
          title: this.$t('product.title'),
          dataIndex: 'productName',
          scopedSlots: {
            customRender: 'productName',
          },
        },
        {
          title: '',
          width: 150,
          scopedSlots: {
            customRender: 'delete',
          },
        },
      ],
      sectionColumns: [
        {
          title: this.$t('product.section'),
          dataIndex: 'title',
          scopedSlots: {
            customRender: 'sectionName',
          },
        },
        {
          title: this.$t('auction.count'),
          width: 200,
          scopedSlots: {
            customRender: 'bindSlots',
          },
        },
        {
          title: this.$t('auction.free'),
          width: 100,
          scopedSlots: {
            customRender: 'freeSlots',
          },
        },
        {
          title: '',
          width: 200,
          scopedSlots: {
            customRender: 'add',
          },
        },
      ],
    }
  },
  computed: {
    isPaid() {
      return this.auctionContent.status === 'PAID'
    },

    defaultExpandedRowKeys() {
      const keys = []
      this.auctionContent.sectionPlaces.forEach(section => {
        if (section.sectionPlaces.length > 0) {
          keys.push(section.id)
        }
      })

      return keys
    },
  },
  methods: {
    getAuctionTypeTitle(code) {
      return this.$t(`auction.lot_types.${code.toLowerCase()}`)
    },

    setPromotionTypeToSelect(typeName) {
      this.selectedPromotion = this.auctionContent.lotPromotions.find(prom => {
        return prom.promotionType.name === typeName
      })
    },

    addSectionProduct(section) {
      this.selectedSection = section
    },

    handleSelect(products) {
      this.selectedProducts = products
    },

    handleSubmit() {
      this.$emit('changePrize', this.selectedProducts)
    },

    handleDeletePosition({ hash_id }, sectionId) {
      const payload = [
        {
          offerHashId: null,
          variationHashId: null,
          placeHashId: hash_id,
          sectionId: sectionId || null,
        },
      ]
      this.$emit('changePrize', payload)
    },
  },
}
</script>
